// Basic
import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
// Store & actions
import useRootOrganizationStore from '@/app/store/rootOrganization';
import actionsSubscriptions from '../../api/actionsSubscriptions';
import actionsPosTerminals from '../../../terminals/api/actionsPosTerminals';
import actionsPlans from '@/app/modules/plans/api/actionsPlans';
// Components
import PlansTable from '@/app/modules/plans/components/tables/PlansTable';
import FooterStep from '@/common-utils/components/renders/modal/components/footers/FooterStep/FooterStep';
import HeaderTitle from '@/common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import Entity from '@/common-utils/components/renders/entity/Entity';
import { Modal, ModalBody, ModalContent } from '@/common-utils/components/ui/modal';
// Common
import getInitialValueForRowSelected from '@/common-utils/components/renders/dataTable/utils/GetInitialValueForRowSelected';
import { useToast } from '@/common-utils/hooks/use-toast';
import useStepConfig from '@/common-utils/hooks/useStepConfig';
// Lang
import { nsSubscription } from '@/app/i18n/namespaces';
import setFiltersEventName from '@/common-utils/components/renders/dataTable/utils/setFiltersEventName';

/**
 * @param {boolean} isOpen
 * @param {function} setIsOpen
 * @param {string} subscriptionId
 * @param {string} clientId
 * @param {string} directProviderId
 * @param {function} setLaunchRefetch
 * @returns {JSX.Element} ModalActivateSubscription
 */
function ModalActivateSubscription({ isOpen, setIsOpen, subscriptionId, clientId, directProviderId, setLaunchRefetch }) {

    // Constants
    const STEPS_NUMBER = 2;
    const STEP_TITLE_PREFIX = 'modal.activate.steps';

    // Toast
    const { toast } = useToast();

    // Store
    const { uuid: loggedUserOrganizationId } = useRootOrganizationStore(s => s.rootOrganization);

    // State
    const [planSelected, setPlanSelected] = useState(getInitialValueForRowSelected());
    const [step, setStep] = useState(0);

    // API Calls
    // Mutations
    const onSuccessAddAllowedClients = () => {
        toast({
            description: <Entity entity={'modal.activate.allow.success'} namespace={nsSubscription} />,
            variant: 'success'
        });
    };
    const submitAddAllowedClients = actionsPlans.addAllowedClients(onSuccessAddAllowedClients, () => { });

    // Async Mutations
    const submitActivateSubscription = actionsSubscriptions.activateSubscription(() => { }, () => { });
    const submitUnblacklistPosTerminal = actionsPosTerminals.unblacklistPosTerminal(() => { }, () => { });

    // Hook
    const { stepTitle, disabledNext, modalBody } = useStepConfig(
        step,
        () => ({
            0: {
                stepTitle: `${STEP_TITLE_PREFIX}.select_plan`,
                disabledNext: !planSelected.rowsData.length,
                modalBody: <PlansTable
                    filtersEventName={setFiltersEventName('plans', true)}
                    organizationId={directProviderId}
                    singleSelection={true}
                    setRowsSelected={setPlanSelected}
                    rowsSelected={planSelected}
                />
            },
            1: {
                stepTitle: `${STEP_TITLE_PREFIX}.activate`,
                disabledNext: false,
                modalBody: <>
                    <Entity entity={'modal.activate.text'} namespace={nsSubscription} data={{ planLabel: planSelected.rowsData[0]?.label }} />
                    <br />
                    <Entity entity={'modal.activate.confirm'} namespace={nsSubscription} />
                </>
            }
        }),
        [planSelected]
    );

    useEffect(() => {
        if (step === 1) {
            submitAddAllowedClients.mutate({
                organizationId: loggedUserOrganizationId,
                planId: planSelected.rowsData[0].uuid,
                allowedClientsIds: [clientId]
            });
        }
    }, [step]);

    return (
        <Modal open={isOpen} onOpenChange={setIsOpen}>
            <ModalContent className={step === 0 ? 'max-w-6xl' : 'max-w-lg'}>
                <HeaderTitle title={'modal.activate.title'} namespace={nsSubscription} />
                <ModalBody className='modal-max-height overflow-auto'>
                    <Entity
                        entity={stepTitle}
                        data={{ step: step + 1, maxStep: STEPS_NUMBER }}
                        namespace={nsSubscription}
                        className={'mb-2 flex'}
                    />
                    {modalBody}
                </ModalBody>
                <FooterStep
                    step={step}
                    setStep={setStep}
                    stepsNumber={STEPS_NUMBER}
                    onValidate={async () => {
                        try {
                            await submitActivateSubscription.mutateAsync({ subscriptionId, planId: planSelected.rowsData[0].uuid });
                            await submitUnblacklistPosTerminal.mutateAsync({ subscriptionId });
                            setLaunchRefetch(true);
                            toast({
                                description: <Entity entity={'modal.activate.success'} namespace={nsSubscription} />,
                                variant: 'success'
                            });
                            setIsOpen(false);
                        }
                        // eslint-disable-next-line no-unused-vars
                        catch (error) {
                            toast({
                                description: <Entity entity={'modal.activate.error'} namespace={nsSubscription} />,
                                variant: 'danger'
                            });
                            setIsOpen(false);
                        }
                    }}
                    isLoading={submitAddAllowedClients.isLoading || submitActivateSubscription.isLoading || submitUnblacklistPosTerminal.isLoading}
                    disableValidation={disabledNext}
                />
            </ModalContent>;
        </Modal>
    );
}

ModalActivateSubscription.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired,
    subscriptionId: PropTypes.string,
    directProviderId: PropTypes.string,
    clientId: PropTypes.string,
    setLaunchRefetch: PropTypes.func
};

export default ModalActivateSubscription;